import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface CancelInterviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  isGroupInterview: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const CONFIRM_DELAY_SECONDS = 3;

export function CancelInterviewDialog({
  open,
  onOpenChange,
  isPending,
  isGroupInterview,
  onConfirm,
  onCancel,
}: CancelInterviewDialogProps) {
  const [countdown, setCountdown] = useState(CONFIRM_DELAY_SECONDS);

  useEffect(() => {
    if (!open) {
      setCountdown(CONFIRM_DELAY_SECONDS);
      return;
    }

    if (countdown <= 0) return;

    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [open, countdown]);

  const canConfirm = countdown <= 0 && !isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-confirm-cancel-interview">
        <DialogHeader>
          <DialogTitle>Cancelar entrevista</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Tem certeza que deseja cancelar esta entrevista? Esta ação é irreversível. A entrevista e
          seus dados serão removidos, e o candidato será notificado.
        </p>
        {isGroupInterview && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Esta é uma entrevista em grupo. O cancelamento afetará todos os entrevistadores do
              grupo.
            </p>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel}>
            Voltar
          </Button>
          <Button
            variant="destructive"
            data-testid="button-confirm-cancel-interview"
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            {isPending
              ? "Cancelando..."
              : countdown > 0
                ? `Confirmar (${countdown}s)`
                : "Confirmar cancelamento"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
